import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  AddressBookResolver,
  AddressResolver,
  AuctionBidsResolver,
  AuctionResolver,
  AuthGuard,
  BalanceResolver,
  CardTokenResolver,
  CountryResolver,
  FantasyGameResolver,
  FantasyHistoryResolver,
  firstTimeUserGuard,
  LottoResolver,
  LottoWalletResolver,
  MaintenanceGuard,
  MemberCardResolver,
  MemberLoyaltyStatusResolver,
  MemberResolver,
  PopulationGuard,
  PortalProductsSubscriptionsResolver,
  PreviewResolver,
  ProviderName,
  PurchaseResolver,
  SeatingDetailResolver,
  TransactionSuccessGuard,
} from '@fgb/core';

// General
import { ContactUsPageComponent } from './pages/contact-us-page/contact-us-page.component';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
// Loyalty
import { AuctionDetailPageComponent } from './pages/auction-detail-page/auction-detail-page.component';
import { AuctionWalletDetailsPageComponent } from './pages/auction-wallet-details-page/auction-wallet-details-page.component';
import { AuctionsPageComponent } from './pages/auctions-page/auctions-page.component';
import { EventsPageComponent } from './pages/events-page/events-page.component';
import { GamesLayoutComponent } from './pages/games-layout/games-layout.component';
import { GamesPageComponent } from './pages/games-page/games-page.component';
import { LottoDetailPageComponent } from './pages/lotto-detail-page/lotto-detail-page.component';
import { LottoWalletDetailsPageComponent } from './pages/lotto-wallet-details-page/lotto-wallet-details-page.component';
import { LottosPageComponent } from './pages/lottos-page/lottos-page.component';
import { LottosSuccessPageComponent } from './pages/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplaceDetailPageComponent } from './pages/marketplace-detail-page/marketplace-detail-page.component';
import { MarketplacePageComponent } from './pages/marketplace-page/marketplace-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { PollsPageComponent } from './pages/polls-page/polls-page.component';
import { PredictorPageComponent, PredictorPlayComponent } from './pages/predictor-page/predictor-page.component';
import { ProductsPageComponent } from './pages/products-page/products-page.component';
import { QuizPageComponent, QuizPlayComponent } from './pages/quiz-page/quiz-page.component';
import { RewardsLayoutComponent } from './pages/rewards-layout/rewards-layout.component';
import { RewardsPageComponent } from './pages/rewards-page/rewards-page.component';
import { WalletPageComponent } from './pages/wallet-page/wallet-page.component';

// Account
import { AccountDetailsPageComponent } from './pages/account-details-page/account-details-page.component';
import { ActivityPageComponent } from './pages/activity-page/activity-page.component';

import { EcashHistoryPageComponent } from './pages/ecash-history/ecash-history.component';
import { EcashFailurePageComponent } from './pages/ecash-page/ecash-failure-page/ecash-failure-page.component';
import { EcashResultPageComponent } from './pages/ecash-page/ecash-result-page/ecash-result-page.component';
import { EcashSuccessPageComponent } from './pages/ecash-page/ecash-success-page/ecash-success-page.component';

// Events & Tickets
import { ContactsPageComponent } from './pages/contacts-page/contacts-page.component';
// Cash Transfer
import { TransferArenaCashPageComponent } from './pages/transfer-arena-cash-page/transfer-arena-cash-page.component';
import { TransferArenaCashSuccessPageComponent } from './pages/transfer-arena-cash-success-page/transfer-arena-cash-success-page.component';
import { TransferMultipleAccountsPageComponent } from './pages/transfer-multiple-accounts-page/transfer-multiple-accounts-page.component';
// Unauthenticated
import { AuthCallbackComponent } from './modules/user/components/auth/auth-callback/auth-callback.component';
import { SingleSignOnComponent } from './modules/user/components/single-sign-on/single-sign-on.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';

// Club Pages
import { EarnPageComponent } from './pages/earn-page/earn-page.component';

// Partners Pages
import { V1SingleSignOnComponent } from './modules/user/components/v1-single-sign-on/v1-single-sign-on.component';
import { CartCheckoutPagePortalProductsComponent } from './pages/cart-checkout-page-portal-products/cart-checkout-page-portal-products.component';
import { CartCheckoutPageComponent } from './pages/cart-checkout-page/cart-checkout-page.component';
import { LottosTermsPageComponent } from './pages/lottos-terms-page/lottos-terms-page.component';
import { MarketplaceTermsPageComponent } from './pages/marketplace-terms-page/marketplace-terms-page.component';
import { SubscriptionsPageComponent } from './pages/subscriptions-page/subscriptions-page.component';
import { TmCallbackComponent } from './shared/components/manage-tickets/tm-callback/tm-callback.component';
import { TmAuthCallbackComponent } from './shared/components/providers/tm/tm-auth-callback/tm-auth-callback.component';
// Offer Pages
import { ScorecardDescriptionComponent } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { provideRegistrationConfig } from '@fgb/portal-component-library/src/lib/registration';
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
import { RegistrationOptOutAlertsComponent } from './modules/registration/pages/steps/registration-opt-out-alerts/registration-opt-out-alerts.component';
import { CampaignDetailsPageComponent } from './pages/campaign-details-page/campaign-details-page.component';
import { CartCheckoutResultsPageComponent } from './pages/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { ChallengesPageComponent } from './pages/challenges-page/challenges-page.component';
import { GalleryPageComponent } from './pages/gallery-page/gallery-page.component';
import { GuardianPageComponent } from './pages/guardian-page/guardian-page.component';
import { HawksBucksPageComponent } from './pages/hawks-bucks-page/hawks-bucks-page.component';
import { MarketplaceWalletPageComponent } from './pages/marketplace-wallet-page/marketplace-wallet-page.component';
import { MyRenewalsPageComponent } from './pages/my-renewals-page/my-renewals-page.component';
import { NotificationManagementPageComponent } from './pages/notification-management-page/notification-management-page.component';
import { PaymentPageComponent } from './pages/payment-page/payment-page.component';
import { PortalProductFailurePageComponent } from './pages/portal-products-pages/portal-product-failure-page/portal-product-failure-page.component';
import { PortalProductResultsPageComponent } from './pages/portal-products-pages/portal-product-results-page/portal-product-results-page.component';
import { PortalProductSuccessPageComponent } from './pages/portal-products-pages/portal-product-success-page/portal-product-success-page.component';
import { PredictorLeaderboardPageComponent } from './pages/predictor-leaderboard-page/predictor-leaderboard-page.component';
import { SplitTransactionPageComponent } from './pages/split-transaction-page/split-transaction-page.component';
import { TicketInvitePageComponentComponent } from './pages/ticket-invite-page-component/ticket-invite-page-component.component';
import { TriviaLeaderboardPageComponent } from './pages/trivia-leaderboard-page/trivia-leaderboard-page.component';
import { WalletClaimedPageComponent } from './pages/wallet-claimed-page/wallet-claimed-page.component';
import { WorksheetPageComponent } from './pages/worksheet-page/worksheet-page.component';
import { ProviderCallbackComponent } from './shared/components/providers/provider-callback/provider-callback.component';
import { Populations } from './shared/utilities/populations';
import { QuizLeaderboardPageComponent } from './pages/quiz-leaderboard-page/quiz-leaderboard-page.component';
import { FantasyGamesPageComponent } from './pages/fantasy-games-page/fantasy-games-page.component';
import { FantasyGamePlayComponent } from './modules/fantasy/components/fantasy-game-play/fantasy-game-play.component';
import { FantasyGameRulesComponent } from './modules/fantasy/components/fantasy-game-rules/fantasy-game-rules.component';
import { FantasyUpNextComponent } from './modules/fantasy/components/fantasy-up-next/fantasy-up-next.component';
import { FantasyGameResultsComponent } from './modules/fantasy/components/fantasy-game-results/fantasy-game-results.component';
import { FantasyLeaderboardPageComponent } from './pages/fantasy-leaderboard-page/fantasy-leaderboard-page.component';
import { CampaignV2DetailsOfferPageComponent } from './pages/campaign-v2-details-offer-page/campaign-v2-details-offer-page.component';
import { CampaignV2DetailsVoucherPageComponent } from './pages/campaign-v2-details-voucher-page/campaign-v2-details-voucher-page.component';

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard, firstTimeUserGuard()],
    canActivateChild: [MaintenanceGuard],
    component: MasterPageComponent,
    resolve: {
      member: MemberResolver,
      memberCard: MemberCardResolver,
      balances: BalanceResolver,
      cardTokens: CardTokenResolver,
    },
    children: [
      {
        path: '',
        component: HomePageComponent,
        resolve: {
          cardTokens: CardTokenResolver,
        },
      },

      {
        path: 'rewards',
        component: RewardsLayoutComponent,
        children: [
          {
            path: '',
            component: RewardsPageComponent,
          },
          {
            path: 'auctions',
            children: [
              {
                path: '',
                resolve: { auctions: AuctionResolver },
                component: AuctionsPageComponent,
              },
              {
                path: 'details/:id',
                resolve: {
                  balances: BalanceResolver,
                  auctions: AuctionResolver,
                },
                component: AuctionDetailPageComponent,
              },
            ],
          },
          {
            path: 'marketplace',
            children: [
              {
                path: '',
                component: MarketplacePageComponent,
              },
              {
                path: 'details/:id',
                children: [
                  {
                    path: '',
                    resolve: {
                      balances: BalanceResolver,
                      cardTokens: CardTokenResolver,
                      country: CountryResolver,
                    },
                    component: MarketplaceDetailPageComponent,
                  },
                  {
                    path: 'terms',
                    component: MarketplaceTermsPageComponent,
                  },
                ],
              },
              {
                path: 'purchase/success',
                component: MarketplacePurchaseSuccessPageComponent,
                resolve: {
                  balances: BalanceResolver,
                },
              },
            ],
          },
          {
            path: 'events',
            component: EventsPageComponent,
          },
          {
            path: 'products',
            component: ProductsPageComponent,
          },
          {
            path: 'raffles',
            canActivate: [PopulationGuard],
            data: {
              exclude: [Populations.Kids],
              popRedirect: '',
            },
            children: [
              {
                path: '',
                component: LottosPageComponent,
                resolve: { balances: BalanceResolver },
              },
              {
                path: 'details/:id',
                children: [
                  {
                    path: '',
                    resolve: { balances: BalanceResolver },
                    component: LottoDetailPageComponent,
                  },
                  {
                    path: 'terms',
                    component: LottosTermsPageComponent,
                    resolve: { lottos: LottoResolver },
                  },
                ],
              },
              {
                path: 'purchase/success',
                canActivate: [TransactionSuccessGuard],
                component: LottosSuccessPageComponent,
              },
            ],
          },
          {
            path: 'wallet',
            children: [
              {
                path: '',
                component: WalletPageComponent,
              },
              {
                path: 'details/:id',
                component: MarketplaceWalletPageComponent,
                resolve: { purchases: PurchaseResolver },
              },
              {
                path: 'raffledetails/:id',
                component: LottoWalletDetailsPageComponent,
                resolve: { lottoWalletItems: LottoWalletResolver },
              },
            ],
          },
          {
            path: 'claimed',
            children: [
              {
                path: '',
                component: WalletClaimedPageComponent,
                resolve: {
                  purchases: PurchaseResolver,
                  lottoWalletItems: LottoWalletResolver,
                  auctionBids: AuctionBidsResolver,
                },
              },
            ],
          },
          {
            path: 'bids',
            children: [
              {
                path: 'details/:id',
                component: AuctionWalletDetailsPageComponent,
                resolve: { bids: AuctionBidsResolver },
              },
            ],
          },
        ],
      },
      { path: 'scorecard-description/:id', component: ScorecardDescriptionComponent },
      {
        path: 'account',
        component: AccountDetailsPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: [Populations.Kids],
          popRedirect: '',
        },
        resolve: {
          member: MemberResolver,
          balances: BalanceResolver,
          seating: SeatingDetailResolver,
          memberLoyaltyStatus: MemberLoyaltyStatusResolver,
        },
      },
      { path: 'activity', component: ActivityPageComponent },
      { path: 'splitTransactions', component: SplitTransactionPageComponent },

      {
        path: 'ecash',
        children: [
          {
            path: 'success',
            resolve: { balances: BalanceResolver },
            component: EcashSuccessPageComponent,
          },
          { path: 'failure', component: EcashFailurePageComponent },
          { path: 'result', component: EcashResultPageComponent },
        ],
      },

      {
        path: 'payment',
        resolve: { balances: BalanceResolver, cardTokens: CardTokenResolver },
        children: [
          {
            path: '',
            component: PaymentPageComponent,
            canActivate: [PopulationGuard],
            data: {
              exclude: [Populations.Kids],
              popRedirect: '',
            },
          },
        ],
      },

      {
        path: 'payment/transactions',
        resolve: {
          balances: BalanceResolver,
          cardTokens: CardTokenResolver,
          memberCard: MemberCardResolver,
          member: MemberResolver,
        },
        component: EcashHistoryPageComponent,
      },

      {
        path: 'cardtoken',
        children: [
          { path: 'success', component: EcashSuccessPageComponent },
          { path: 'failure', component: EcashFailurePageComponent },
          { path: 'result', component: EcashResultPageComponent },
        ],
      },

      {
        path: 'campaign',
        children: [
          {
            path: 'details/:offerId',
            component: CampaignDetailsPageComponent,
          },
        ],
      },
      {
        path: 'campaign-v2',
        children: [
          {
            path: 'details/offer/:offerId',
            component: CampaignV2DetailsOfferPageComponent,
          },
          {
            path: 'details/voucher/:voucherId',
            component: CampaignV2DetailsVoucherPageComponent,
          },
        ],
      },
      { path: 'faq', component: FaqPageComponent },
      { path: 'terms', component: TermsPageComponent },
      { path: 'contact', component: ContactUsPageComponent },
      { path: 'challenges', component: ChallengesPageComponent },
      { path: 'my-renewals', component: MyRenewalsPageComponent },
      {
        path: 'gallery',
        component: GalleryPageComponent,
        canActivate: [PopulationGuard],
        data: {
          include: [Populations.Kids],
          popRedirect: '',
        },
      },
      {
        path: 'worksheets',
        component: WorksheetPageComponent,
        canActivate: [PopulationGuard],
        data: {
          include: [Populations.Kids],
          popRedirect: '',
        },
      },
      {
        path: 'guardians',
        component: GuardianPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: [Populations.Kids],
          popRedirect: '',
        },
      },

      {
        path: 'hawksbucks',
        component: HawksBucksPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: [Populations.Kids],
          popRedirect: '',
        },
      },

      {
        path: 'portalproducts',
        resolve: { balances: BalanceResolver, cardTokens: CardTokenResolver },
        children: [
          {
            path: 'success',
            resolve: { balances: BalanceResolver },
            component: PortalProductSuccessPageComponent,
          },
          { path: 'failure', component: PortalProductFailurePageComponent },
          { path: 'result', component: PortalProductResultsPageComponent },
        ],
      },

      {
        path: 'contacts',
        component: ContactsPageComponent,
        resolve: { addressBookResolver: AddressBookResolver },
      },
      {
        path: 'games',
        component: GamesLayoutComponent,
        children: [
          { path: '', component: GamesPageComponent },
          { path: 'polls', component: PollsPageComponent },
          {
            path: 'trivia',
            component: QuizPageComponent,
            children: [
              { path: '', component: QuizPlayComponent },
              { path: 'leaderboard', component: QuizLeaderboardPageComponent },
            ],
          },
          { path: 'trivia/leaderboard', component: TriviaLeaderboardPageComponent },
          {
            path: 'predictor',
            component: PredictorPageComponent,
            canActivate: [PopulationGuard],
            data: {
              exclude: [Populations.Kids],
              popRedirect: '',
            },
            children: [
              { path: '', component: PredictorPlayComponent },
              { path: 'leaderboard', component: PredictorLeaderboardPageComponent },
            ],
          },
          {
            path: 'pickaplayer',
            component: FantasyGamesPageComponent,
            resolve: { games: FantasyGameResolver, history: FantasyHistoryResolver },
            children: [
              { path: '', component: FantasyGamePlayComponent },
              { path: 'how-to-play', component: FantasyGameRulesComponent },
              { path: 'leaderboard', component: FantasyLeaderboardPageComponent },
              { path: 'up-next', component: FantasyUpNextComponent },
              { path: 'rules', component: FantasyGameRulesComponent },
              { path: 'results', component: FantasyGameResultsComponent },
            ],
          },
          {
            path: 'pickaplayer/:id',
            component: FantasyGamesPageComponent,
            resolve: { games: FantasyGameResolver },
          },
        ],
      },
      // Club Pages
      {
        path: 'checkout',
        children: [
          {
            path: '',
            component: CartCheckoutPageComponent,
            resolve: {
              member: MemberResolver,
              address: AddressResolver,
              country: CountryResolver,
            },
          },
          {
            path: 'success',
            component: CartCheckoutSuccessPageComponent,
          },
          {
            path: 'results',
            component: CartCheckoutResultsPageComponent,
          },
        ],
        resolve: {
          address: AddressResolver,
        },
      },
      {
        path: 'subscriptions',
        children: [
          {
            path: '',
            component: SubscriptionsPageComponent,
            resolve: {
              subscriptions: PortalProductsSubscriptionsResolver,
              address: AddressResolver,
            },
          },
          {
            path: 'checkout',
            component: CartCheckoutPagePortalProductsComponent,
            resolve: {
              subscriptions: PortalProductsSubscriptionsResolver,
              address: AddressResolver,
              country: CountryResolver,
              member: MemberResolver,
            },
          },
        ],
      },
      { path: 'notificationmanagement', component: NotificationManagementPageComponent },
      { path: 'earn', component: EarnPageComponent },
      {
        path: 'transfer',
        canActivate: [PopulationGuard],
        data: {
          exclude: [Populations.Kids],
          popRedirect: '',
        },
        children: [
          {
            path: '',
            component: TransferArenaCashPageComponent,
            resolve: { balances: BalanceResolver },
          },
          {
            path: 'multipleaccounts',
            component: TransferMultipleAccountsPageComponent,
          },
          {
            path: 'success',
            component: TransferArenaCashSuccessPageComponent,
            resolve: { balances: BalanceResolver },
          },
        ],
      },
      { path: '404page', component: NotFoundPageComponent },
      { path: 'signin-ticketmaster', component: TmCallbackComponent },
      {
        path: 'provider/:providerid',
        component: ProviderCallbackComponent,
      },
    ],
  },

  {
    path: 'ticketinvite',
    component: TicketInvitePageComponentComponent,
  },

  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: LoginPageComponent, resolve: { preview: PreviewResolver }, canActivate: [MaintenanceGuard] },
  { path: 'login/accounts', component: LoginAccountsPageComponent, canActivate: [MaintenanceGuard] },

  {
    path: 'register',
    component: RegistrationLayoutPageComponent,
    loadChildren: () => import('@fgb/portal-component-library/src/lib/registration').then((m) => m.RegistrationModule),
    providers: [
      provideRegistrationConfig({
        routes: [
          // Custom pages
          {
            path: 'optoutalerts',
            component: RegistrationOptOutAlertsComponent,
          },
          {
            path: 'payment',
            data: {
              showExpressCheckout: true,
              expressCheckoutProviders: [ProviderName.Paze],
            },
          },
        ],
      }),
    ],
  },

  { path: 'callback', component: AuthCallbackComponent },
  { path: 'sso', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId/:clubId', component: SingleSignOnComponent },
  { path: 'login/ss/login.aspx', component: V1SingleSignOnComponent },
  { path: 'maintenance', component: MaintenancePageComponent },
  { path: 'tm-auth-callback', component: TmAuthCallbackComponent },
  { path: '**', redirectTo: '404page' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
